import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	
	getDDLLabelByValue,
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { EmployeeService } from "../../../services/EmployeeService";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { ReportsService } from "../../../services/ReportsService";
import { YearToDateRemittedRevenuQuebecReportDataGridWithColumnToggled } from "./YearToDateRemittedRevenuQuebecReportDataGridWithColumnToggled";
import { YearToDateReportDataGridWithColumnToggled } from "./YearToDateReportDataGridWithColumnToggled";
import { YearToDateReportHeader } from "./YearToDateReportHeader";
import { YearToDateService } from "../../../services/YearToDateService";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";
import FileSaver from "file-saver";

export interface IYearToDateReportSetupPage { }
const YEAR_TO_DATE_REPORT_QUERY_KEY = "year-to-date-report";


const useYearToDateReport = (
	runGetListAPICall: boolean,
	clientId: string,
	taxYear: number,
	employee: string,
	employeeType: string,
	payPeriod: string,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			YEAR_TO_DATE_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,

			taxYear,
			employee,
			employeeType,
			payPeriod,
			params,
		],
		queryFn: () =>
			ReportsService.getYearToDateReport(
				runGetListAPICall,
				clientId,
				taxYear,
				employeeType,
				payPeriod,
				employee,

				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["employee-status-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetYearsDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["year-to-date-ddl", clientId, taxYear],
		queryFn: (data) => {
			return YearToDateService.getYearsDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const YearToDateReport: React.FC<IYearToDateReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [yearTODateReportData, setYearToDateReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);


	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});


	const { isLoading: isYearTODateDDLLoading, data: yearsDDLItems } = useGetYearsDDL(clientId, taxYear);

	const selectedYear = formMethods.watch("selectedYear");
	const employee = formMethods.getValues("employee");
	const employeeType = formMethods.getValues("employeeType");
	const payPeriod = formMethods.getValues("payPeriod");

	const { isPending, data } = useYearToDateReport(
		runGetListAPICall,
		clientIdStore,
		selectedYear && getDDLLabelByValue(yearsDDLItems, selectedYear),
		employee && employee,
		employeeType && employeeType,
		payPeriod && payPeriod,
		queryParams,
		navigateToLogout
	);
	const yearToDateList: any | null = data;
	useEffect(() => {
		if (yearToDateList) {
			setYearToDateReportData(yearToDateList);
		}
	}, [yearToDateList]);

	const { isLoading: isEmployeeDDLLoading, data: employeeDDL } = useGetEmployeeDDL(clientId);

	const { isLoading: isPayPeriodDDLLoading, data: payPeriodDDL } = useGetPayFrequencyDDL(clientId, taxYear);



	const employeeDDLItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	const yearDDLItems = useMemo(() => {
		if (yearsDDLItems) {
			if (
				yearsDDLItems &&
				Array.isArray(yearsDDLItems)
			) {
				let resources: IDropDownListResponse[] =
					yearsDDLItems.map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [yearsDDLItems]);

	const payPeriodDDLItems = useMemo(() => {
		if (payPeriodDDL) {
			if (
				payPeriodDDL &&
				Array.isArray(payPeriodDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payPeriodDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payPeriodDDL]);


	//   after click apply button api calls with updated values and update year to date report data
	const yearToDateReportDataAPICall = (
		taxYear: any,
		employee: string,
		employeeType: string,
		payPeriod: string,
	) => {
	
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const response: any = await ReportsService.getYearToDateReport(
					true,
					clientIdStore,
					getDDLLabelByValue(yearsDDLItems, taxYear),
					employee,
					employeeType,
					payPeriod,
					queryParams,
					navigateToLogout
				);

				if (response) {
					setYearToDateReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		yearToDateReportDataAPICall(
			values.selectedYear,
			values.employee,
			values.employeeType,
			values.payPeriod,
		);

	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getYearToDateAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [YEAR_TO_DATE_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedYearToDateListItems = useMemo(() => {
		return (
			yearTODateReportData &&
			yearTODateReportData?.main_ytd_report?.items &&
			yearTODateReportData?.main_ytd_report?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [yearTODateReportData]);

	const updatedYearToDateRemittedRevenuListItems = useMemo(() => {
		return (
			yearTODateReportData &&
			yearTODateReportData?.remitted_ytd_report?.items &&
			yearTODateReportData?.remitted_ytd_report?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [yearTODateReportData]);

	const expandedLabels = ["Reports", "Misc"];
	const expandedYearToDateListOrSetupItem = "Year To Date";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Misc" &&
				item.label === expandedYearToDateListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedYearToDateListOrSetupItem && "/reports/year-to-date",
			};
			return enhancedItem;
		});
	};


	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"selectedYear",
		"employee",
		"employeeType",
		"payPeriod",

	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [selectedYear, employee, employeeType, payPeriod] = values;

		try {
			setIsLoading(true);
			const response = await ReportsService.exportYearToDateReport(
				selectedOption,
				clientIdStore,
				getDDLLabelByValue(yearsDDLItems, selectedYear),
				employee,
				employeeType,
				payPeriod,
				queryParams
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - yearToDate-Report.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - yearToDate-Report.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName} - yearToDate-Report.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}

	};


	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Year to Date Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<YearToDateReportHeader
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
						employeeDDLItems={employeeDDLItems}
						yearDDLItems={yearsDDLItems}
						payPeriodDDLItems={payPeriodDDLItems}
						taxYear={taxYear}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isLoading ||
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isYearTODateDDLLoading ||
						isEmployeeDDLLoading ||
						isPayPeriodDDLLoading

					}
					testId="year-to-date-report"
				/>

				<YearToDateReportDataGridWithColumnToggled
					columns={
						yearTODateReportData?.main_ytd_report.headerColumns
							? yearTODateReportData.main_ytd_report.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// yearTODateReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						yearTODateReportData
							&& yearTODateReportData
								?.main_ytd_report.totalHeaderColumns &&
							Object.keys(yearTODateReportData?.main_ytd_report.totalHeaderColumns).length !== 0
							? yearTODateReportData?.main_ytd_report.totalHeaderColumns
							: {}
						// yearTODateReportData?.main_ytd_report.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						yearTODateReportData && updatedYearToDateListItems
							? updatedYearToDateListItems
							: []
						// yearTODateReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						yearTODateReportData && yearTODateReportData.main_ytd_report.total
							? yearTODateReportData.main_ytd_report.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Year to Date Report"}
					onExportButtonClick={onExportButtonClick}
				/>

				<YearToDateRemittedRevenuQuebecReportDataGridWithColumnToggled
					columns={
						yearTODateReportData?.remitted_ytd_report.headerColumns
							? yearTODateReportData.remitted_ytd_report.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// yearTODateReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						yearTODateReportData
							&& yearTODateReportData
								?.remitted_ytd_report.totalHeaderColumns &&
							Object.keys(yearTODateReportData?.remitted_ytd_report.totalHeaderColumns).length !== 0
							? yearTODateReportData?.remitted_ytd_report.totalHeaderColumns
							: {}
						// yearTODateReportData?.remitted_ytd_report.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						yearTODateReportData && updatedYearToDateRemittedRevenuListItems
							? updatedYearToDateRemittedRevenuListItems
							: []
						// yearTODateReportData?.items ?? []

					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						yearTODateReportData && yearTODateReportData.remitted_ytd_report.total
							? yearTODateReportData.remitted_ytd_report.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Year to Date Report"}
					// yearToDateDataList={yearTODateReportData}
					selectedYear={taxYear}
				/>
				{yearTODateReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
